import React, { useEffect, useState } from 'react';
import { MIDDLE_STYLE } from '@constants/responsive';
import { GLOBAL_THEME_COLOR } from '@constants/theme';
import { Button, Col, Divider, Input, InputNumber, Row, Skeleton, Space, TimePicker } from 'antd';
import { useParams } from 'react-router';
import { LocalLocation, Reservation, ReservationStatus } from '@core/models';
import { heroApiService, bookingApiService } from '@services/service-register';
import {
  CalendarModal,
  CardSectionHeader,
  DateTimeScheduleEditor,
  LoadableContainer,
} from '@components/index';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  MailOutlined,
  PhoneOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import * as dayjs from 'dayjs';
import { BRAND_NAME, BRAND_URL } from '@constants/brand';
import { useBreakpoint } from '@core/hooks';
import { useAppDispatch } from '@core/configureStore';
import { enqueueNotification, openModal } from '@core/features';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { NewGenericReservationPayload } from '@services/booking-api.service';

enum FormStage {
  DateTimeSelect = 0,
  PersonalInfo = 1,
}

type ReservationPayload = {
  firstName: string;
  lastName: string;
  emailContact: string;
  phone: string;
  numberOfPeople: number;
  date: number;
  timeHour: number;
  timeMinute: number;
  table: number;
  floor: number;
};

const validationSchema = {
  firstName: yup.string().max(30, 'Invalid name length').required('First name is required'),
  lastName: yup.string().max(30, 'Invalid name length').required('Last name is required'),
  emailContact: yup.string().email('Enter a valid email'),
  phone: yup.string().max(13, 'Invalid phone length'),
};

const initialValue: Record<keyof ReservationPayload, string> = {
  date: '',
  emailContact: '',
  firstName: '',
  lastName: '',
  numberOfPeople: '',
  timeHour: '',
  timeMinute: '',
  phone: '',
  floor: '',
  table: '',
};

const ReservationFormContainer: React.FC<{
  propsLocationId?: string;
  existingReservation?: Reservation<any>;
  onUpdated?: () => void;
}> = ({ existingReservation, propsLocationId, onUpdated }) => {
  const dispatch = useAppDispatch();
  const today = moment();
  const [stage, setStage] = useState(FormStage.DateTimeSelect);
  const { locationId } = useParams<{ locationId: string }>();
  const { isMobile } = useBreakpoint();
  const [inputError, setInputError] =
    useState<Record<keyof ReservationPayload, string>>(initialValue);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [location, setLocation] = useState<LocalLocation | undefined>(undefined);
  const formik = useFormik<ReservationPayload>({
    initialValues: {
      firstName: '',
      lastName: '',
      emailContact: '',
      phone: '',
      numberOfPeople: 1,
      date: today.unix(),
      timeHour: today.hours(),
      timeMinute: today.minutes(),
      floor: 0,
      table: 0,
    },
    onSubmit: async values => {
      try {
        setMutating(true);
        const reservedDate = moment.unix(values.date);
        const reservedTime = moment({
          hour: values.timeHour,
          minute: values.timeMinute,
          day: reservedDate.get('date'),
          month: reservedDate.get('month'),
        });
        const params: NewGenericReservationPayload = {
          numberOfPeople: values.numberOfPeople,
          email: values.emailContact,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
          reservedDate: reservedTime.unix(),
          reservationStatus: ReservationStatus.Waiting,
        };
        if (existingReservation) {
          await bookingApiService.updateReservation(existingReservation.id, {
            ...params,
            reservationStatus: existingReservation.status,
          });
        } else {
          await bookingApiService.createNewReservation(propsLocationId || locationId, params);
        }
        formik.resetForm();
        onUpdated && onUpdated();
        dispatch(
          enqueueNotification({
            name: 'Your reservation is successfully recorded',
            description: 'Your reservation is successfully recorded',
            type: 'Success',
          })
        );
      } catch (e) {
        dispatch(
          enqueueNotification({
            name: 'Failed to add reservation',
            description: 'Failed to add reservation',
            type: 'Error',
          })
        );
      }
      setMutating(false);
    },
  });

  const handleSetScheduleTime = (scheduledTime: dayjs.Dayjs | null, _: string | string[]) => {
    if (!scheduledTime) return;
    handleInputChange('timeHour', scheduledTime.hour());
    handleInputChange('timeMinute', scheduledTime.minute());
  };

  const handleSetScheduleDate = () => {
    dispatch(
      openModal({
        modalName: 'calendarModal',
      })
    );
  };

  const handleInputChange = async (fieldName: keyof ReservationPayload, value: any) => {
    await formik.setFieldValue(fieldName, value);
  };

  useEffect(() => {
    const handleInputError = async () => {
      for (const fieldName of Object.keys(validationSchema)) {
        let updatedInputError = inputError;
        try {
          const value = (formik.values as any)[fieldName];
          await (validationSchema as any)[fieldName].validate(value);
          // @ts-ignore
          updatedInputError[fieldName] = '';
        } catch (error: any) {
          // @ts-ignore
          updatedInputError[fieldName] = error.message;
        }
        setInputError(updatedInputError);
      }
    };
    handleInputError();
  }, [formik.values]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const _location = await heroApiService.getLocationById(propsLocationId || locationId);
        setLocation(_location);
        setLoading(false);
      } catch (e) {
        console.log(e);
      }
    };
    init();
  }, [locationId, propsLocationId]);

  useEffect(() => {
    const init = async () => {
      if (!existingReservation) return;
      handleInputChange('emailContact', existingReservation.email);
      handleInputChange('firstName', existingReservation.firstName);
      handleInputChange('lastName', existingReservation.lastName);
      handleInputChange('numberOfPeople', existingReservation.numberOfPeople);
      handleInputChange('phone', existingReservation.phone);

      const reservedDate = moment(existingReservation.reserved_date);
      handleInputChange('date', reservedDate.unix());
      handleInputChange('timeHour', reservedDate.hours());
      handleInputChange('timeMinute', reservedDate.minutes());
    };
    init();
  }, [existingReservation]);

  return (
    <div>
      <LoadableContainer isLoading={loading} loadComponent={<Skeleton />}>
        <div>
          <p style={{ fontSize: 'larger' }}>{location?.title}</p>
          <p style={{ color: GLOBAL_THEME_COLOR.$dark_text_color }}>{location?.full_address}</p>
        </div>
      </LoadableContainer>
      <Divider />
      <Space size={25} direction={'vertical'} style={{ width: '100%' }}>
        {(stage === FormStage.DateTimeSelect || !!existingReservation) && (
          <React.Fragment>
            <div style={{ marginTop: 10 }}>
              <CardSectionHeader title="NUMBER OF PEOPLE" />
              <InputNumber
                id={'numberOfPeople'}
                name={'numberOfPeople'}
                required
                min={1}
                max={100}
                value={formik.values.numberOfPeople}
                status={!!inputError.phone ? 'error' : ''}
                style={{ width: '100%', height: 50, ...MIDDLE_STYLE, marginTop: 10 }}
                size={'large'}
                onChange={value => handleInputChange('numberOfPeople', value || 1)}
                placeholder={'Enter number of people'}
                prefix={<UsergroupAddOutlined />}
              />
              {!!inputError.phone && (
                <p style={{ marginTop: 10, color: GLOBAL_THEME_COLOR.$error_color }}>
                  {inputError.phone}
                </p>
              )}
            </div>
            <DateTimeScheduleEditor
              date={formik.values.date}
              time={{ hour: formik.values.timeHour, minute: formik.values.timeMinute }}
              scheduleLaterEnabled={true}
              handleSetScheduleTime={handleSetScheduleTime}
              handleSetScheduleDate={handleSetScheduleDate}
            />
            {!existingReservation && (
              <Button
                type={'primary'}
                size={'large'}
                loading={mutating}
                disabled={
                  !!inputError['date'] ||
                  !!inputError['numberOfPeople'] ||
                  !!inputError['timeHour'] ||
                  !!inputError['timeMinute']
                }
                onClick={() => setStage(FormStage.PersonalInfo)}
                style={{ width: '100%', ...MIDDLE_STYLE, padding: '20px 0px' }}>
                Continue <ArrowRightOutlined style={{ marginLeft: 10 }} />
              </Button>
            )}
          </React.Fragment>
        )}
        {(stage === FormStage.PersonalInfo || !!existingReservation) && (
          <React.Fragment>
            <Row gutter={10} style={{ marginTop: 10 }}>
              <Col span={isMobile ? 24 : 12}>
                <CardSectionHeader title="First Name" />
                <Input
                  required
                  id={'firstName'}
                  name={'firstName'}
                  status={!!inputError.firstName ? 'error' : ''}
                  value={formik.values.firstName}
                  onChange={e => handleInputChange('firstName', e.target.value)}
                  style={{ width: '100%', marginTop: 10 }}
                  placeholder={'Enter your first name'}
                  prefix={<UserOutlined />}
                />
                {!!inputError.firstName && (
                  <p style={{ marginTop: 10, color: GLOBAL_THEME_COLOR.$error_color }}>
                    {inputError.firstName}
                  </p>
                )}
              </Col>
              <Col span={isMobile ? 24 : 12}>
                <CardSectionHeader title="Last Name" />
                <Input
                  required
                  id={'lastName'}
                  name={'lastName'}
                  status={!!inputError.firstName ? 'error' : ''}
                  value={formik.values.lastName}
                  onChange={e => handleInputChange('lastName', e.target.value)}
                  style={{ width: '100%', marginTop: 10 }}
                  placeholder={'Enter your last name'}
                  prefix={<></>}
                />
                {!!inputError.lastName && (
                  <p style={{ marginTop: 10, color: GLOBAL_THEME_COLOR.$error_color }}>
                    {inputError.lastName}
                  </p>
                )}
              </Col>
            </Row>
            <Row gutter={10}>
              <Col span={isMobile ? 24 : 12}>
                <CardSectionHeader title="Email contact" />
                <Input
                  value={formik.values.emailContact}
                  type={'email'}
                  status={!!inputError.emailContact ? 'error' : ''}
                  onChange={e => handleInputChange('emailContact', e.target.value)}
                  style={{ width: '100%', marginTop: 10 }}
                  placeholder={'Enter your email address'}
                  prefix={<MailOutlined />}
                />
                {!!inputError.emailContact && (
                  <p style={{ marginTop: 10, color: GLOBAL_THEME_COLOR.$error_color }}>
                    {inputError.emailContact}
                  </p>
                )}
              </Col>
              <Col span={isMobile ? 24 : 12}>
                <CardSectionHeader title="Phone" />
                <Input
                  type={'phone'}
                  value={formik.values.phone}
                  onChange={e => handleInputChange('phone', e.target.value)}
                  style={{ width: '100%', marginTop: 10 }}
                  placeholder={'Enter your phone number'}
                  prefix={<PhoneOutlined />}
                />
              </Col>
            </Row>
            {existingReservation &&
              (existingReservation.status === ReservationStatus.Waiting ||
                existingReservation.status === ReservationStatus.InProgress) && (
                <React.Fragment>
                  <Button
                    type={'primary'}
                    size={'large'}
                    loading={mutating}
                    disabled={
                      !!inputError['firstName'] ||
                      !!inputError['lastName'] ||
                      !!inputError['table'] ||
                      !!inputError['floor'] ||
                      (!!inputError['emailContact'] && !!inputError['phone'])
                    }
                    onClick={() => formik.handleSubmit()}
                    style={{ width: '100%', ...MIDDLE_STYLE, padding: '20px 0px' }}>
                    Update Reservation
                  </Button>
                </React.Fragment>
              )}
            {!existingReservation && (
              <React.Fragment>
                <Button
                  type={'primary'}
                  size={'large'}
                  loading={mutating}
                  disabled={
                    !!inputError['firstName'] ||
                    !!inputError['lastName'] ||
                    !!inputError['table'] ||
                    !!inputError['floor'] ||
                    (!!inputError['emailContact'] && !!inputError['phone'])
                  }
                  onClick={() => formik.handleSubmit()}
                  style={{ width: '100%', ...MIDDLE_STYLE, padding: '20px 0px' }}>
                  Book Now <ArrowRightOutlined style={{ marginLeft: 10 }} />
                </Button>
                <Button
                  size={'large'}
                  onClick={() => setStage(FormStage.DateTimeSelect)}
                  style={{ width: '100%', ...MIDDLE_STYLE, padding: '20px 0px' }}>
                  <ArrowLeftOutlined /> Go Back
                </Button>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        <div style={{ ...MIDDLE_STYLE, justifyContent: 'flex-end' }}>
          <a href={BRAND_URL} style={{ color: '#1677ff' }} target="_blank" rel="noreferrer">
            powered by {BRAND_NAME}
          </a>
        </div>
      </Space>
      <CalendarModal onSelected={date => handleInputChange('date', date.unix())} />
    </div>
  );
};

export default ReservationFormContainer;
